import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useState } from "react";

const PromoBanner = () => {
  const { settings } = useSiteSettings();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || settings.promo_banner_enabled !== "true" || !settings.promo_banner_title) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative overflow-hidden gold-shimmer-banner"
    >
      {/* Multi-layer background */}
      <div className="absolute inset-0 bg-gradient-to-r from-[hsl(40_90%_28%)] via-primary to-[hsl(40_90%_28%)]" />
      
      {/* Sweeping shimmer */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background: "linear-gradient(105deg, transparent 20%, hsl(40 80% 70% / 0.6) 45%, hsl(40 100% 85% / 0.8) 50%, hsl(40 80% 70% / 0.6) 55%, transparent 80%)",
          backgroundSize: "300% 100%",
          animation: "bannerShimmer 2.5s ease-in-out infinite",
        }}
      />

      {/* Top/bottom gold borders */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[hsl(40_80%_65%)] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[hsl(40_80%_65%)] to-transparent" />
      
      <Link
        to={settings.promo_banner_link || "/book"}
        className="block relative z-10"
      >
        <div className="container mx-auto px-6 py-4 sm:py-5 flex items-center justify-center gap-4 text-center">
          <motion.div
            animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <Sparkles size={20} className="text-[hsl(40_100%_85%)] shrink-0 drop-shadow-[0_0_6px_hsl(40_80%_60%/0.8)]" />
          </motion.div>
          
          <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-4">
            <span className="font-serif text-base sm:text-xl font-bold italic text-primary-foreground drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
              {settings.promo_banner_title}
            </span>
            
            {settings.promo_banner_subtitle && (
              <>
                <span className="hidden sm:block w-[1px] h-4 bg-primary-foreground/30" />
                <span className="text-[11px] sm:text-sm tracking-[0.15em] uppercase text-[hsl(40_100%_90%)] font-medium">
                  {settings.promo_banner_subtitle}
                </span>
              </>
            )}
            
            {settings.promo_banner_detail && (
              <>
                <span className="hidden sm:block w-[1px] h-4 bg-primary-foreground/30" />
                <span className="text-[11px] sm:text-sm font-bold text-[hsl(40_100%_90%)] tracking-wide">
                  {settings.promo_banner_detail}
                </span>
              </>
            )}
            
            <motion.div
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight size={16} className="hidden sm:block text-[hsl(40_100%_85%)]" />
            </motion.div>
          </div>
          
          <motion.div
            animate={{ rotate: [0, -15, 15, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3, delay: 1 }}
          >
            <Sparkles size={20} className="text-[hsl(40_100%_85%)] shrink-0 hidden sm:block drop-shadow-[0_0_6px_hsl(40_80%_60%/0.8)]" />
          </motion.div>
        </div>
      </Link>
      
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDismissed(true); }}
        className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full hover:bg-primary-foreground/20 transition-colors text-primary-foreground/70 hover:text-primary-foreground"
        aria-label="Dismiss banner"
      >
        <X size={16} />
      </button>
    </motion.div>
  );
};

export default PromoBanner;
